import React from 'react';
import {
    Dialog,
    DialogContent,
    Box,
    Typography,
    LinearProgress,
    Alert
} from '@mui/material';
import { Upload, AlertTriangle } from 'lucide-react';

/**
 * UploadProgressDialog - Shows upload progress with warning
 *
 * @param {boolean} open - Dialog open state
 * @param {number} progress - Upload progress (0-100)
 * @param {string} fileName - Name of file being uploaded
 * @param {number} totalFiles - Total number of files
 * @param {number} currentFile - Current file number
 */
const UploadProgressDialog = ({
    open,
    progress = 0,
    fileName = '',
    totalFiles = 1,
    currentFile = 1
}) => {
    const isMultipleFiles = totalFiles > 1;

    return (
        <Dialog
            open={open}
            maxWidth="sm"
            fullWidth
            disableEscapeKeyDown
            PaperProps={{
                sx: {
                    borderRadius: 3,
                    p: 2
                }
            }}
        >
            <DialogContent>
                {/* Warning Alert */}
                <Alert
                    severity="warning"
                    icon={<AlertTriangle />}
                    sx={{
                        mb: 3,
                        borderRadius: 2,
                        '& .MuiAlert-icon': {
                            fontSize: 28
                        }
                    }}
                >
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        ⚠️ Do Not Close This Tab!
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                        Upload in progress. Closing this tab will cancel the upload.
                    </Typography>
                </Alert>

                {/* Upload Icon */}
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        mb: 2
                    }}
                >
                    <Box
                        sx={{
                            width: 80,
                            height: 80,
                            borderRadius: '50%',
                            bgcolor: 'primary.light',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            animation: 'pulse 2s ease-in-out infinite',
                            '@keyframes pulse': {
                                '0%, 100%': { opacity: 1 },
                                '50%': { opacity: 0.6 }
                            }
                        }}
                    >
                        <Upload size={40} color="#1976d2" />
                    </Box>
                </Box>

                {/* File Info */}
                <Typography
                    variant="h6"
                    align="center"
                    sx={{
                        fontWeight: 600,
                        mb: 1,
                        color: 'text.primary'
                    }}
                >
                    Uploading {isMultipleFiles ? 'Files' : 'File'}...
                </Typography>

                {isMultipleFiles && (
                    <Typography
                        variant="body2"
                        align="center"
                        sx={{ mb: 2, color: 'text.secondary' }}
                    >
                        File {currentFile} of {totalFiles}
                    </Typography>
                )}

                {fileName && (
                    <Typography
                        variant="body2"
                        align="center"
                        sx={{
                            mb: 3,
                            color: 'text.secondary',
                            fontStyle: 'italic',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                        }}
                    >
                        {fileName}
                    </Typography>
                )}

                {/* Progress Bar */}
                <Box sx={{ mb: 2 }}>
                    <LinearProgress
                        variant="determinate"
                        value={progress}
                        sx={{
                            height: 12,
                            borderRadius: 6,
                            backgroundColor: 'grey.200',
                            '& .MuiLinearProgress-bar': {
                                borderRadius: 6,
                                background: 'linear-gradient(90deg, #1976d2 0%, #42a5f5 100%)'
                            }
                        }}
                    />
                </Box>

                {/* Progress Percentage */}
                <Typography
                    variant="h5"
                    align="center"
                    sx={{
                        fontWeight: 700,
                        color: 'primary.main',
                        mb: 1
                    }}
                >
                    {Math.round(progress)}%
                </Typography>

                {/* Status Text */}
                <Typography
                    variant="body2"
                    align="center"
                    sx={{ color: 'text.secondary' }}
                >
                    {progress < 100
                        ? 'Please wait while your files are being uploaded...'
                        : 'Upload complete! Processing files...'}
                </Typography>
            </DialogContent>
        </Dialog>
    );
};

export default UploadProgressDialog;
